import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { saveAs } from 'file-saver';
import { ScribeButton as Button } from '../../components/scribe/ScribeUI';
import { getClientConsents, downloadConsentEvidence } from '../../services/clientConsentService';
import ClientConsentDialog from './ClientConsentDialog';
export const CONSENT_CHANGED = 'scribe:client-consent-changed';
export const consentDate = value => {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  }) : 'Not recorded';
};
export default function ClientConsentPanel({
  patientId
}) {
  const [record, setRecord] = useState(null),
    [offset, setOffset] = useState(0),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(''),
    [actionError, setActionError] = useState(''),
    [form, setForm] = useState(null),
    [downloading, setDownloading] = useState('');
  const generation = useRef(0);
  const load = useCallback(async () => {
    const version = ++generation.current;
    setLoading(true);
    setError('');
    setRecord(null);
    try {
      const data = await getClientConsents(patientId, offset);
      if (version === generation.current) setRecord({
        ...data,
        patientId
      });
    } catch (e) {
      if (version === generation.current) setError(e.message);
    } finally {
      if (version === generation.current) setLoading(false);
    }
  }, [patientId, offset]);
  useEffect(() => {
    setOffset(0);
    setForm(null);
    setActionError('');
  }, [patientId]);
  useEffect(() => {
    void load();
    return () => {
      generation.current += 1;
    };
  }, [load]);
  useEffect(() => {
    const focus = () => {
      if (!form) void load();
    };
    const visible = () => {
      if (document.visibilityState === 'visible') focus();
    };
    window.addEventListener('focus', focus);
    document.addEventListener('visibilitychange', visible);
    return () => {
      window.removeEventListener('focus', focus);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [load, form]);
  const visible = record?.patientId === patientId ? record : null;
  const close = () => {
    setForm(null);
    void load();
  };
  const saved = () => {
    setForm(null);
    setActionError('');
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED, {
      detail: {
        patientId
      }
    }));
    if (offset !== 0) setOffset(0);else void load();
  };
  const download = async event => {
    setDownloading(event.id);
    setActionError('');
    const version = generation.current;
    try {
      const result = await downloadConsentEvidence(patientId, event.document);
      if (version === generation.current) saveAs(result.blob, result.name);
    } catch (e) {
      if (version === generation.current) setActionError(e.message);
    } finally {
      setDownloading('');
    }
  };
  return <Box component="section" aria-label="Consent log" sx={{
    bgcolor: '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: '12px',
    p: 2.5,
    mt: 2.5
  }}>
  <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" sx={{
      mb: 1.5
    }}>
   <Typography sx={{
        fontSize: 14,
        fontWeight: 600
      }}>Consent log</Typography><Button variant="outlined" size="small" onClick={() => setForm({
        mode: 'consent_granted'
      })}>Add consent</Button>
  </Stack>
  <Typography variant="body2" color="text.secondary" sx={{
      mb: 2
    }}>View MindVault sharing consent and consent recorded by your team.</Typography>
  {loading ? <CircularProgress size={24} aria-label="Loading consent records" /> : error ? <Alert severity="error" action={<Button size="small" onClick={load}>Retry</Button>}>{error}</Alert> : visible && <>
   {typeof visible.sharing === 'boolean' && <Chip size="small" variant="outlined" label={`MindVault sharing: ${visible.sharing ? 'On' : 'Off'}`} sx={{
        mb: 2
      }} />}
   {visible.sharing_error && <Alert severity="warning" sx={{
        mb: 2
      }}>MindVault sharing history is unavailable. Return to this tab to retry.</Alert>}
   {!visible.events.length ? <Typography variant="body2" color="text.secondary">No consent entries recorded.</Typography> : <Stack spacing={1.5}>{visible.events.map(event => {
          const granted = event.event === 'consent_granted',
            withdrawn = event.event === 'consent_revoked',
            manual = event.source === 'manual';
          return <Box key={event.id} sx={{
            border: '1px solid #E5E7EB',
            borderRadius: 2,
            p: 2
          }}>
     <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{
              mb: 1
            }}><Typography variant="body2" fontWeight={600}>{manual ? granted ? 'Consent given' : withdrawn ? 'Consent withdrawn' : 'Consent document attached' : granted ? 'MindVault sharing consent given' : 'MindVault sharing consent withdrawn'}</Typography>
      <Chip size="small" variant="outlined" label={event.channel === 'in_app' ? 'In-app' : event.channel === 'written' ? 'Written' : 'Verbal'} />{granted && event.withdrawn && <Chip size="small" label="Withdrawn" />}
     </Stack>
     <Typography variant="caption" color="text.secondary">{granted ? 'Consent date' : withdrawn ? 'Withdrawal date' : 'Attachment date'}</Typography><Typography variant="body2">{consentDate(event.occurred_at)}</Typography>
     {event.notes && <Typography variant="body2" sx={{
              mt: 1,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere'
            }}>{event.notes}</Typography>}
     {!manual && <Typography variant="caption" color="text.secondary" component="p" sx={{
              mt: 1
            }}>Source: MindVault profile sharing. Historical dates may not be available.</Typography>}
     {manual && event.channel === 'written' && !withdrawn && <Box sx={{
              mt: 1
            }}>{event.document ? <Button size="small" onClick={() => download(event)} disabled={downloading === event.id}>{downloading === event.id ? 'Downloading…' : 'Download document'}</Button> : <Typography variant="body2" color="text.secondary">No document attached</Typography>}</Box>}
     {manual && granted && <Stack direction="row" gap={1} flexWrap="wrap" sx={{
              mt: 1
            }}>
      {!event.withdrawn && <Button size="small" onClick={() => setForm({
                mode: 'consent_revoked',
                target: event
              })}>Record withdrawal</Button>}
      {event.channel === 'written' && !event.document && <Button size="small" onClick={() => setForm({
                mode: 'document_attached',
                target: event
              })}>Attach document</Button>}
     </Stack>}
    </Box>;
        })}</Stack>}
   {visible.total > 25 && <Stack direction="row" gap={1.5} justifyContent="flex-end" alignItems="center" sx={{
        mt: 2
      }}>
    <Button size="small" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 25))}>Previous</Button><Typography variant="body2">{offset + 1}–{Math.min(offset + 25, visible.total)} of {visible.total}</Typography>
    <Button size="small" disabled={offset + 25 >= visible.total} onClick={() => setOffset(offset + 25)}>Next</Button>
   </Stack>}
  </>}
  {actionError && <Alert severity="error" sx={{
      mt: 2
    }}>{actionError}</Alert>}
  {form && <ClientConsentDialog key={`${patientId}:${form.mode}:${form.target?.id || 'new'}`} patientId={patientId} {...form} onClose={close} onSaved={saved} />}
 </Box>;
}
